import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Power } from "lucide-react";
import { useServices } from "@/hooks/useServices";
import type { Service } from "@/types/service";

interface ServicesTableProps {
  services: Service[];
  isLoading: boolean;
  onEdit: (id: string) => void;
}

const serviceTypeLabels: Record<Service["service_type"], string> = {
  maintenance: "Manutenção",
  repair: "Reparo",
  installation: "Instalação",
  calibration: "Calibração",
  inspection: "Inspeção",
  other: "Outro",
};

export function ServicesTable({ services, isLoading, onEdit }: ServicesTableProps) {
  const { deactivateService } = useServices();

  const formatCurrency = (value?: number) => {
    if (!value) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  if (isLoading) {
    return <div className="text-center py-8">Carregando serviços...</div>;
  }

  if (services.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Nenhum serviço cadastrado
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Código</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Preço de Custo</TableHead>
            <TableHead>Preço de Venda</TableHead>
            <TableHead>Duração (h)</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {services.map((service) => (
            <TableRow key={service.id}>
              <TableCell>{service.service_code || "-"}</TableCell>
              <TableCell className="font-medium">{service.service_name}</TableCell>
              <TableCell>
                <Badge variant="outline">
                  {serviceTypeLabels[service.service_type]}
                </Badge>
              </TableCell>
              <TableCell>{formatCurrency(service.cost_price)}</TableCell>
              <TableCell>{formatCurrency(service.sale_price)}</TableCell>
              <TableCell>{service.estimated_duration_hours || "-"}</TableCell>
              <TableCell>
                <Badge variant={service.active ? "default" : "secondary"}>
                  {service.active ? "Ativo" : "Inativo"}
                </Badge>
              </TableCell>
              <TableCell className="text-right space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(service.id)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                {service.active && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deactivateService(service.id)}
                  >
                    <Power className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
